import React from 'react';
import { Phone, Mail, FileText, Star, CreditCard } from 'lucide-react';
import type { BookingDetails } from '@/hooks/booking/useBookingDetails';

interface BookingDetailsSectionProps {
  booking: BookingDetails;
}

export const BookingDetailsSection = ({ booking }: BookingDetailsSectionProps) => {
  return (
    <div className="space-y-6">
      {/* Vendor Information */}
      {booking.vendor && (
        <div className="bg-card rounded-lg border p-6 space-y-6">
          <h2 className="text-lg font-semibold">Service Provider</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Email</p>
                <p className="text-sm text-muted-foreground">
                  {booking.vendor.email}
                </p>
              </div>
            </div>

            {booking.vendor.phone && (
              <div className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Phone</p>
                  <p className="text-sm text-muted-foreground">
                    {booking.vendor.phone}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Notes and Details */}
      {(booking.note || booking.notes || booking.completion_notes || booking.decline_reason || booking.completion) && (
        <div className="bg-card rounded-lg border p-6 space-y-6">
          <h2 className="text-lg font-semibold">Notes</h2>
          
          <div className="space-y-4">
            {/* Customer Notes */}
            {(booking.note || booking.notes) && (
              <div className="flex items-start gap-3">
                <FileText className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Customer Notes</p>
                  <div className="mt-1 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="text-sm text-blue-900 dark:text-blue-100 whitespace-pre-wrap">
                      {booking.note || booking.notes}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Decline Reason */}
            {booking.decline_reason && (
              <div className="flex items-start gap-3">
                <FileText className="h-4 w-4 text-destructive mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-destructive">Decline Reason</p>
                  <div className="mt-1 p-3 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200 dark:border-red-800">
                    <p className="text-sm text-red-900 dark:text-red-100 whitespace-pre-wrap">
                      {booking.decline_reason}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Completion Notes */}
            {booking.completion_notes && (
              <div className="flex items-start gap-3">
                <FileText className="h-4 w-4 text-purple-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-purple-700 dark:text-purple-400">Completion Details</p>
                  <div className="mt-1 p-3 bg-purple-50 dark:bg-purple-950/20 rounded-lg border border-purple-200 dark:border-purple-800">
                    <p className="text-sm text-purple-900 dark:text-purple-100 whitespace-pre-wrap">
                      {booking.completion_notes}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Completion Data */}
            {booking.completion && (
              <div className="flex items-start gap-3">
                <FileText className="h-4 w-4 text-purple-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-purple-700 dark:text-purple-400">Service Completion</p>
                  <div className="mt-1 p-3 bg-purple-50 dark:bg-purple-950/20 rounded-lg border border-purple-200 dark:border-purple-800 space-y-3">
                    {booking.completion.comment && (
                      <div>
                        <p className="text-xs font-medium text-purple-800 dark:text-purple-300 mb-1">Vendor Comments:</p>
                        <p className="text-sm text-purple-900 dark:text-purple-100 whitespace-pre-wrap">
                          {booking.completion.comment}
                        </p>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 border-t border-purple-200 dark:border-purple-700">
                      <div className="flex items-center gap-2">
                        <Star className="h-4 w-4 text-yellow-500" />
                        <div>
                          <p className="text-xs font-medium text-purple-800 dark:text-purple-300">Rating</p>
                          <p className="text-sm text-purple-900 dark:text-purple-100">{booking.completion.rating}/5</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-purple-600" />
                        <div>
                          <p className="text-xs font-medium text-purple-800 dark:text-purple-300">Payment</p>
                        <p className="text-sm text-purple-900 dark:text-purple-100">
                          Ksh {Number(booking.completion.payment_amount).toLocaleString()}
                        </p>
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-xs font-medium text-purple-800 dark:text-purple-300">Status</p>
                        <p className="text-sm text-purple-900 dark:text-purple-100 capitalize">
                          {booking.completion.payment_status}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};